import { HttpClient } from '@angular/common/http';
import { Component, OnInit, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, Observable, Subject, Subscription, asyncScheduler, concat, defer, firstValueFrom, timer } from 'rxjs';
import { catchError, concatMap, exhaustMap, filter, ignoreElements, map, throttleTime } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TemaService } from 'src/app/services/tema.services';
import { SseService } from 'src/app/services/SseService';
import { FmpIntradiaService } from 'src/app/services/fmp-intradia.service';
import { MetaApiDownloadFase } from 'src/app/services/meta-api.service';
import { MetaapiCandlesSyncService } from 'src/app/services/metaapi-candles-sync.service';
import { periodoToTimeframe } from 'src/app/utils/periodo.util';
import {
  createChart, CandlestickSeries, HistogramSeries,
  ColorType, CrosshairMode, IChartApi,
} from 'lightweight-charts';
import { ChartDrawingController } from '../../common/chart-drawing/chart-drawing-controller';
import { getToolLabel } from '../../common/chart-drawing/drawing-tools.catalog';
import { DrawingTextDialogComponent } from '../../common/chart-drawing/drawing-text-dialog/drawing-text-dialog.component';

@Component({
  selector: 'app-grafica-analisis',
  templateUrl: './grafica-analisis.component.html',
  styleUrl: './grafica-analisis.component.scss'
})
export class GraficaAnalisisComponent implements OnInit, OnChanges, OnDestroy {
  private subDarktema = new Subscription();
  public isDark = false;

  // LWC instances
  private lwChart: IChartApi | null = null;
  private candleSeries: any = null;
  private volumeSeries: any = null;
  private resizeObserver: ResizeObserver | null = null;
  private fullData: number[][] = [];

  // Drawing tools (lógica compartida estilo TradingView)
  public drawingCtl = new ChartDrawingController();
  private ohlcCache: any[] = [];
  private currentCompania = '';

  // Selector de periodos (temporalidad de velas) — ids de PeriodoEnum del backend
  public readonly periodos: { label: string; id: number }[] = [
    { label: '1m', id: 1 }, { label: '5m', id: 2 }, { label: '15m', id: 3 }, { label: '30m', id: 4 },
    { label: '1H', id: 5 }, { label: '4H', id: 7 }, { label: 'D', id: 6 },
  ];
  public activePeriodo = 6;
  public loading = false;
  public noData = false;
  /** Días hacia atrás a pedir por periodo intradía, para no traer cientos de miles de velas. */
  private readonly VENTANA_DIAS_POR_PERIODO: Record<number, number> = { 1: 10, 2: 30, 3: 60, 4: 90, 5: 120, 7: 365 };
  private requestSeq = 0;

  // Descarga de datos cuando la serie está vacía
  public descargando = false;
  public progresoTexto = '';
  /** 0-100 si se conoce el total esperado; null → barra indeterminada. */
  public progresoValor: number | null = null;
  private descargaSub: Subscription | null = null;
  private descargaClave: string | null = null;
  private descargaEsFmp = false;
  /** La descarga terminó y la fuente no tiene velas para esta serie. */
  public sinDatosFuente = false;
  /** Serie MetaAPI vacía y sin credenciales para descargarla. */
  public faltaMetaapi = false;
  /** Series ya descargadas en esta sesión: no se reintentan solas. */
  private descargasIntentadas = new Set<string>();
  /** Inicio (ms) de la ventana intradía mostrada; null en diario. */
  private ventanaDesde: number | null = null;
  private lotes$ = new Subject<string>();
  private lotesSub: Subscription;
  private cargandoLotes = false;
  private lotesPendiente: { clave: string; final: boolean } | null = null;

  // ── Rango "Todo" en intradía: carga más allá de la ventana inicial (VENTANA_DIAS_POR_PERIODO) ──
  /** true mientras setRange('all') está trayendo velas más antiguas que la ventana intradía. */
  public rangeLoading = false;
  /** Tope de historial que se trae para "Todo" en intradía (~2 años) — el resto queda solo vía descarga manual. */
  private readonly RANGE_ALL_INTRADAY_CAP_MS = 730 * 24 * 60 * 60 * 1000;
  private readonly RANGE_ALL_MAX_CHUNKS = 100;

  // Actualización de las últimas velas (MetaAPI intradía)
  public enVivo = false;
  private liveSub: Subscription | null = null;
  /** Cada cuánto se piden las últimas velas a MetaAPI, por periodo. */
  private readonly POLL_MS_POR_PERIODO: Record<number, number> = { 1: 15000, 2: 30000, 3: 60000, 4: 60000, 5: 120000, 7: 120000 };

  get isIntraday(): boolean { return this.activePeriodo !== 6; }

  /**
   * Velas premarket (tbl_historicos_extendido). Solo FMP intradía: MetaAPI no las tiene y el diario
   * no tiene premarket. Son solo para ver: el backend las añade a la serie de la gráfica y ningún
   * cálculo las lee.
   */
  public premarket = this.leerPremarket();
  get puedePremarket(): boolean { return !this.metaapi && this.isIntraday; }
  private get premarketActivo(): boolean { return this.premarket && this.puedePremarket; }

  togglePremarket(): void {
    this.premarket = !this.premarket;
    try { localStorage.setItem('nectum:grafica-analisis:premarket', this.premarket ? '1' : '0'); } catch {}
    if (this.currentCompania) this.fetchData(this.currentCompania);
  }

  private leerPremarket(): boolean {
    try { return localStorage.getItem('nectum:grafica-analisis:premarket') === '1'; } catch { return false; }
  }

  get activePeriodoLabel(): string {
    return this.periodos.find(p => p.id === this.activePeriodo)?.label ?? 'D';
  }

  @Input() compania: string = '';
  /** Mercado MetaAPI: los históricos salen de metahistoricos en vez de historicosm. */
  @Input() metaapi: boolean = false;
  /** idNectum del activo; sin él no se puede descargar ni actualizar. */
  @Input() idnectum: number | null = null;

  constructor(
    private _temaService: TemaService,
    private _httcliet: HttpClient,
    private _sseService: SseService,
    private dialog: MatDialog,
    private _toastr: ToastrService,
    private _fmpIntradia: FmpIntradiaService,
    private _metaapiSync: MetaapiCandlesSyncService,
  ) {
    // Los lotes pueden llegar muy seguidos: se relee el backend como mucho cada 1,5 s.
    this.lotesSub = this.lotes$
      .pipe(throttleTime(1500, asyncScheduler, { leading: true, trailing: true }))
      .subscribe(clave => this.cargarLotes(clave));
  }

  ngOnInit() {
    this.subDarktema = this._temaService.valDarkTheme.subscribe({
      next: (data: any) => {
        this.isDark = !!data;
        this.updateChartTheme();
      }
    });
    this.fetchData(this.compania || 'AAPL');
  }

  ngOnChanges(changes: SimpleChanges) {
    const cambio = (k: string) => !!changes[k] && !changes[k].firstChange;
    if (cambio('compania') || cambio('metaapi')) {
      this.fetchData(this.compania);
    } else if (cambio('idnectum') && !this.liveSub && this.debeActualizar()) {
      // Mismo activo, pero ahora se conoce su idNectum (p. ej. elegido desde su mercado).
      this.iniciarActualizacion();
    } else if (cambio('idnectum') && this.noData && !this.descargando && !this.loading
               && !this.descargasIntentadas.has(this.claveActual())) {
      // Serie vacía cuyo idNectum no se conocía: ya se puede descargar.
      this.iniciarDescarga();
    }
  }

  async fetchData(compania: string) {
    this.currentCompania = compania;
    this.detenerActualizacion();
    if (this.descargaClave && this.descargaClave !== this.claveActual()) this.cancelarDescarga();
    this.sinDatosFuente = false;
    this.faltaMetaapi = false;
    const clave = this.claveActual();
    const url = this.urlVentana();
    const seq = ++this.requestSeq;
    this.loading = true;
    try {
      const data = await this._sseService.apiGetNew(url).toPromise();
      if (seq !== this.requestSeq) return; // respuesta obsoleta (se cambió periodo/compañía)
      this.noData = !Array.isArray(data) || data.length === 0;
      if (this.noData) {
        this.fullData = [];
        this.ohlcCache = [];
        this.destroyChart();
        // Sin históricos: se descargan solos (una vez por serie, para no entrar en bucle).
        if (this.descargaClave === clave) {
          // ya se está descargando esta misma serie
        } else if (this.descargasIntentadas.has(clave)) {
          this.sinDatosFuente = true;
        } else {
          this.iniciarDescarga();
        }
      } else if (document.getElementById('analisisChartContainer')) {
        await this.buildChart(data, compania);
        if (this.debeActualizar()) this.iniciarActualizacion();
      }
    } catch (error) {
      console.error('Error al recuperar datos:', error);
    } finally {
      if (seq === this.requestSeq) this.loading = false;
    }
  }

  setPeriodo(id: number): void {
    if (id === this.activePeriodo) return;
    this.activePeriodo = id;
    if (this.currentCompania) this.fetchData(this.currentCompania);
  }

  /** Identifica la serie mostrada; sirve para descartar resultados de una serie anterior. */
  private claveActual(): string {
    // Sin el premarket: es la misma serie, y cambiarlo no debe cancelar ni repetir su descarga.
    return `${this.currentCompania}|${this.activePeriodo}|${this.metaapi}`;
  }

  private urlSerie(): string {
    return 'historicos-compania?iso=' + encodeURIComponent(this.currentCompania)
      + '&metaapi=' + this.metaapi + '&periodo=' + this.activePeriodo
      + (this.premarketActivo ? '&premarket=true' : '');
  }

  /**
   * URL de la ventana inicial. En intradía se acota con `desde`; no se manda `hasta` porque las
   * velas MetaAPI se guardan en hora del broker y pueden ir por delante de la hora UTC actual.
   */
  private urlVentana(): string {
    if (!this.isIntraday) {
      this.ventanaDesde = null;
      return this.urlSerie();
    }
    this.ventanaDesde = Date.now() - (this.VENTANA_DIAS_POR_PERIODO[this.activePeriodo] ?? 30) * 86400000;
    return this.urlSerie() + '&desde=' + this.ventanaDesde;
  }

  // ── Descarga automática (serie vacía) ─────────────────────────────────────

  /** Solo para MetaAPI sin credenciales: abre el login y, si se conecta, descarga. */
  conectarMetaapi(): void {
    this.iniciarDescarga(true);
  }

  /**
   * Descarga la serie en la fuente que corresponda. Las dos fuentes van de lo más reciente a
   * lo más antiguo y guardan lote a lote: tras cada lote se relee el backend (cargarLotes), así
   * que el último lote se ve enseguida y los anteriores se van añadiendo por la izquierda.
   */
  private iniciarDescarga(interactivo = false): void {
    if (!this.idnectum || this.descargando) return;
    const idnectum = this.idnectum;
    const iso = this.currentCompania;
    const clave = this.claveActual();

    const lote = () => this.lotes$.next(clave);
    const fin = () => {
      if (this.descargaClave !== clave) return;
      this.descargaSub = null;
      this.descargaClave = null;
      this.descargando = false;
      this.progresoTexto = '';
      this.progresoValor = null;
      this.cargarLotes(clave, true);
    };

    this.descargando = true;
    this.descargaClave = clave;
    this.faltaMetaapi = false;
    this.sinDatosFuente = false;
    this.progresoValor = null;
    this.descargaEsFmp = !this.metaapi && this.isIntraday;

    if (this.metaapi) {
      const timeframe = periodoToTimeframe(this.activePeriodo);
      let esperadas = 0;
      let fase: MetaApiDownloadFase | '' = '';
      this.progresoTexto = 'Conectando con MetaAPI…';
      this.descargaSub = this._metaapiSync.asegurarCredenciales(interactivo).pipe(
        concatMap(accountId => {
          if (!accountId) { this.faltaMetaapi = true; return EMPTY; }
          this.descargasIntentadas.add(clave);
          this.progresoTexto = `Descargando ${iso} (MetaAPI)…`;
          return this._metaapiSync.sincronizar({ iso, idNectum: idnectum }, timeframe, accountId, {
            onTotal: n => { esperadas = n; },
            onFase: f => { fase = f; },
            onBatch: (_batch, total) => {
              const etiqueta = this.faseLabel(fase);
              this.progresoTexto = `Descargando ${iso}${etiqueta ? ' — ' + etiqueta : ''} — ${total}${esperadas ? '/' + esperadas : ''} velas`;
              this.progresoValor = esperadas ? Math.min(100, (total * 100) / esperadas) : null;
            },
          });
        })
      ).subscribe({ next: lote, complete: fin, error: fin });
    } else if (this.isIntraday) {
      this.descargasIntentadas.add(clave);
      this.progresoTexto = `Descargando ${iso} (FMP)…`;
      this.descargaSub = this._fmpIntradia.descargar(0, idnectum, this.activePeriodo).subscribe({
        next: ev => {
          if (ev.tipo === 'progreso') { this.progresoTexto = `Descargando ${ev.data}`; lote(); }
          else if (ev.tipo === 'aviso') this._toastr.warning(ev.data, 'FMP', { timeOut: 6000 });
          else if (ev.tipo === 'error') this._toastr.error(ev.data, 'FMP', { timeOut: 6000 });
        },
        complete: fin,
        error: fin,
      });
    } else {
      this.descargasIntentadas.add(clave);
      this.progresoTexto = `Descargando histórico diario de ${iso}…`;
      this.descargaSub = this._sseService.apiGetNew(`descargarDatos?idmercado=0&idnectum=${idnectum}`).subscribe({
        complete: fin,
        error: (err: any) => {
          console.error('Error descargando histórico diario:', err);
          this._toastr.error(`No se pudo descargar el histórico de ${iso}.`, 'Descarga', { timeOut: 6000 });
          fin();
        },
      });
    }
  }

  /**
   * Relee del backend lo que ya se guardó. Sin gráfica todavía → pinta la ventana (el último
   * lote). Con gráfica → trae solo lo anterior a la vela más antigua y lo antepone.
   * Con `final` (fin de la descarga) decide el estado resultante.
   */
  private async cargarLotes(clave: string, final = false): Promise<void> {
    if (clave !== this.claveActual()) return;
    if (this.cargandoLotes) {
      this.lotesPendiente = { clave, final: final || (this.lotesPendiente?.final ?? false) };
      return;
    }
    this.cargandoLotes = true;
    try {
      if (!this.fullData.length) {
        const data = await firstValueFrom(this._sseService.apiGetNew(this.urlVentana()));
        if (clave === this.claveActual() && !this.fullData.length && Array.isArray(data) && data.length
            && document.getElementById('analisisChartContainer')) {
          this.noData = false;
          await this.buildChart(data, this.currentCompania);
        }
      } else if (!this.ventanaCompleta()) {
        let url = this.urlSerie() + '&hasta=' + (this.velaMasAntiguaMs() - 1);
        if (this.ventanaDesde !== null) url += '&desde=' + this.ventanaDesde;
        const data = await firstValueFrom(this._sseService.apiGetNew(url));
        if (clave === this.claveActual()) this.anteponerVelas(data);
      }
    } catch (e) {
      console.warn('Error cargando lote de velas', e);
    } finally {
      this.cargandoLotes = false;
    }

    if (final && clave === this.claveActual()) {
      if (!this.fullData.length) {
        this.noData = true;
        this.sinDatosFuente = !this.faltaMetaapi;
      } else if (this.debeActualizar()) {
        this.iniciarActualizacion();
      }
    }

    const pendiente = this.lotesPendiente;
    this.lotesPendiente = null;
    if (pendiente) this.cargarLotes(pendiente.clave, pendiente.final);
  }

  private velaMasAntiguaMs(): number {
    let min = Infinity;
    for (const r of this.fullData) min = Math.min(min, Number(r[0]));
    return min;
  }

  /** En intradía la gráfica no pasa del inicio de la ventana, aunque la descarga siga. */
  private ventanaCompleta(): boolean {
    return this.ventanaDesde !== null && this.velaMasAntiguaMs() <= this.ventanaDesde;
  }

  /** Añade velas anteriores a la serie sin mover lo que el usuario está viendo. */
  private anteponerVelas(rows: number[][]): void {
    if (!Array.isArray(rows) || !rows.length || !this.lwChart || !this.candleSeries || !this.volumeSeries) return;
    const existentes = new Set(this.fullData.map(r => Number(r[0])));
    const nuevas = rows.filter(r => !existentes.has(Number(r[0])));
    if (!nuevas.length) return;

    const antes = this.ohlcCache.length;
    const rango = this.lwChart.timeScale().getVisibleLogicalRange();
    const c = this.getColors();
    this.fullData = [...nuevas, ...this.fullData].sort((a, b) => Number(a[0]) - Number(b[0]));
    this.ohlcCache = this.convertOhlc(this.fullData);
    try {
      this.candleSeries.setData(this.ohlcCache);
      this.volumeSeries.setData(this.convertVolume(this.fullData, c.upColor, c.downColor));
      const añadidas = this.ohlcCache.length - antes;
      if (rango && añadidas > 0) {
        this.lwChart.timeScale().setVisibleLogicalRange({ from: rango.from + añadidas, to: rango.to + añadidas });
      }
    } catch (e) { console.warn('No se pudieron anteponer las velas', e); }
  }

  private cancelarDescarga(): void {
    this.lotesPendiente = null;
    this.descargaSub?.unsubscribe();
    if (this.descargaEsFmp) this._fmpIntradia.cerrar();
    this.descargaSub = null;
    this.descargaClave = null;
    this.descargando = false;
    this.progresoTexto = '';
    this.progresoValor = null;
  }

  private faseLabel(fase: MetaApiDownloadFase | ''): string {
    switch (fase) {
      case 'hueco': return 'rellenando hueco interno';
      case 'cola-antigua': return 'completando historial antiguo';
      case 'reciente': return 'actualizando datos recientes';
      case 'inicial': return 'descarga inicial';
      default: return '';
    }
  }

  // ── Actualización de últimas velas (MetaAPI intradía) ─────────────────────

  private debeActualizar(): boolean {
    return this.metaapi && this.isIntraday && !!this.idnectum && this.fullData.length > 0 && !this.descargando;
  }

  /**
   * Primero completa lo que falte entre la última vela guardada y ahora; después, cada
   * POLL_MS, persiste las últimas velas cerradas de MetaAPI, las relee del backend y encima
   * pinta la vela en formación, que solo vive en memoria. Sin credenciales no hace nada.
   */
  private iniciarActualizacion(): void {
    const idnectum = this.idnectum;
    if (!idnectum) return;
    this.detenerActualizacion();
    const clave = this.claveActual();
    const compania = { iso: this.currentCompania, idNectum: idnectum };
    const timeframe = periodoToTimeframe(this.activePeriodo);
    const pollMs = this.POLL_MS_POR_PERIODO[this.activePeriodo] ?? 60000;

    this.liveSub = this._metaapiSync.asegurarCredenciales(false).pipe(
      concatMap(accountId => {
        if (!accountId || clave !== this.claveActual()) return EMPTY;
        this.enVivo = true;
        return concat(
          this._metaapiSync.sincronizar(compania, timeframe, accountId).pipe(ignoreElements()),
          // timer(0, …): la primera pasada pinta ya la vela en formación, sin esperar un intervalo.
          timer(0, pollMs).pipe(
            filter(() => !document.hidden),
            exhaustMap(() => this._metaapiSync.actualizarUltimas(compania, timeframe, accountId).pipe(
              // Primero las cerradas del backend y después la vela en formación: aplicarVelas solo
              // actualiza la última o añade detrás, y si la nueva en formación entrara antes, la
              // recién cerrada se quedaría con sus valores intermedios.
              concatMap(enFormacion => concat(
                this.refrescarDesdeBackend(clave),
                defer(() => {
                  if (clave === this.claveActual()) this.aplicarVelas(enFormacion);
                  return EMPTY;
                })
              )),
              catchError(err => { console.warn('[MetaAPI] Error actualizando últimas velas', err); return EMPTY; })
            ))
          )
        );
      })
    ).subscribe({
      error: err => { console.warn('[MetaAPI] Actualización detenida', err); this.enVivo = false; },
    });
  }

  private detenerActualizacion(): void {
    this.liveSub?.unsubscribe();
    this.liveSub = null;
    this.enVivo = false;
  }

  private refrescarDesdeBackend(clave: string): Observable<void> {
    if (!this.fullData.length) return EMPTY;
    let ultimaMs = 0;
    for (const r of this.fullData) ultimaMs = Math.max(ultimaMs, Number(r[0]));
    const url = 'historicos-compania?iso=' + encodeURIComponent(this.currentCompania)
      + '&metaapi=true&periodo=' + this.activePeriodo + '&desde=' + ultimaMs;
    return this._sseService.apiGetNew(url).pipe(
      map((rows: any) => { if (clave === this.claveActual()) this.aplicarVelas(rows); })
    );
  }

  /** Actualiza/añade velas al final de la serie sin reconstruir la gráfica. */
  private aplicarVelas(rows: number[][]): void {
    if (!Array.isArray(rows) || !rows.length || !this.candleSeries || !this.volumeSeries) return;
    const c = this.getColors();
    const ordenadas = [...rows].sort((a, b) => Number(a[0]) - Number(b[0]));
    for (const r of ordenadas) {
      const bar = this.convertOhlc([r])[0];
      if (!bar) continue;
      const ultima = this.ohlcCache[this.ohlcCache.length - 1];
      const cmp = ultima ? this.compareTime(bar.time, ultima.time) : 1;
      if (cmp < 0) continue; // lightweight-charts solo admite actualizar la última vela o añadir detrás

      const ms = Number(r[0]);
      let idx = -1;
      for (let i = this.fullData.length - 1; i >= 0; i--) {
        if (Number(this.fullData[i][0]) === ms) { idx = i; break; }
      }
      if (idx >= 0) this.fullData[idx] = r; else this.fullData.push(r);

      if (cmp === 0) this.ohlcCache[this.ohlcCache.length - 1] = bar; else this.ohlcCache.push(bar);
      const prevClose = this.ohlcCache.length > 1 ? this.ohlcCache[this.ohlcCache.length - 2].close : bar.close;
      try {
        this.candleSeries.update(bar);
        this.volumeSeries.update({
          time: bar.time,
          value: isFinite(Number(r[r.length - 1])) ? Number(r[r.length - 1]) : 0,
          color: bar.close >= prevClose ? c.upColor + '88' : c.downColor + '88',
        });
      } catch (e) { console.warn('No se pudo actualizar la vela', e); }
    }
  }

  async buildChart(data: number[][], compania: string) {
    this.fullData = Array.isArray(data) ? [...data] : [];
    this.currentCompania = compania;
    this.destroyChart();

    const container = document.getElementById('analisisChartContainer');
    if (!container) return;

    const c = this.getColors();
    const ohlc = this.convertOhlc(this.fullData);
    const vol = this.convertVolume(this.fullData, c.upColor, c.downColor);

    const rect = container.getBoundingClientRect();
    this.lwChart = createChart(container, {
      width: rect.width || window.innerWidth,
      height: rect.height || window.innerHeight - 100,
      layout: {
        background: { type: ColorType.Solid, color: c.bg },
        textColor: c.text,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: c.grid },
        horzLines: { color: c.grid },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: c.border },
      timeScale: { borderColor: c.border, timeVisible: this.isIntraday, secondsVisible: false },
      handleScroll: { mouseWheel: true, pressedMouseMove: true },
      handleScale: { mouseWheel: true, pinch: true, axisPressedMouseMove: true },
    });

    this.candleSeries = this.lwChart.addSeries(CandlestickSeries, {
      upColor: c.upColor,
      downColor: c.downColor,
      borderVisible: false,
      wickUpColor: c.upColor,
      wickDownColor: c.downColor,
    });
    this.candleSeries.setData(ohlc);
    this.candleSeries.applyOptions({ title: compania });

    this.volumeSeries = this.lwChart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
    });
    this.lwChart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    this.volumeSeries.setData(vol);

    // Drawing tools (toolbar TV, edición, persistencia)
    this.ohlcCache = ohlc;
    try {
      this.drawingCtl.attach({
        chart: this.lwChart,
        series: this.candleSeries,
        container,
        storageKey: () =>
          this.currentCompania ? `nectum:drawings:v1:${this.currentCompania}:${this.activePeriodoLabel}` : null,
        formatTime: (ms: number) => this.toTime(ms) as any,
        getBarOHLC: (time: any) => {
          const target = String(time);
          const bar = this.ohlcCache.find((b: any) => String(b.time) === target);
          return bar ? { open: bar.open, high: bar.high, low: bar.low, close: bar.close } : null;
        },
        requestText: (toolType: string) => {
          const ref = this.dialog.open(DrawingTextDialogComponent, {
            data: { toolLabel: getToolLabel(toolType) },
          });
          return new Promise(resolve => {
            ref.afterClosed().subscribe((value: string | undefined) => resolve(value === undefined ? null : value));
          });
        },
        isDark$: this._temaService.valDarkTheme,
      });
      this.drawingCtl.restoreFromStorage();
    } catch (e) { console.warn('Drawing controller init error', e); }

    // ResizeObserver
    this.resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      const w = entry?.contentRect?.width || container.clientWidth;
      const h = entry?.contentRect?.height || container.clientHeight;
      if (w > 0 && h > 0) { try { this.lwChart!.resize(w, h); } catch {} }
    });
    this.resizeObserver.observe(container);

    setTimeout(() => { try { this.lwChart!.timeScale().fitContent(); } catch {} }, 50);
  }

  // ── Range selector ────────────────────────────────────────────────────────

  /**
   * Para diario ya se pide la serie completa sin ventana (urlVentana() no manda `desde`), así
   * que fitContent() alcanza. En intradía la carga inicial está acotada a
   * VENTANA_DIAS_POR_PERIODO y nada dispara más fetch al hacer scroll — sin esto, "Todo" solo
   * encuadraba lo ya cargado. Pagina hacia atrás con `hasta=<vela más antigua>-1` (sin `desde`,
   * para poder ir más allá de `ventanaDesde`) hasta agotar la fuente o llegar al tope de ~2 años.
   */
  private async cargarHistorialCompletoIntradia(): Promise<void> {
    const clave = this.claveActual();
    const cutoffMs = Date.now() - this.RANGE_ALL_INTRADAY_CAP_MS;
    let guard = 0;
    while (guard++ < this.RANGE_ALL_MAX_CHUNKS) {
      if (clave !== this.claveActual()) return;
      const oldest = this.velaMasAntiguaMs();
      if (!Number.isFinite(oldest) || oldest <= cutoffMs) return;
      const url = this.urlSerie() + '&hasta=' + (oldest - 1);
      let data: any;
      try {
        data = await firstValueFrom(this._sseService.apiGetNew(url));
      } catch (e) { console.warn('Error cargando histórico completo intradía', e); return; }
      if (clave !== this.claveActual()) return;
      if (!Array.isArray(data) || !data.length) return;
      const antes = this.fullData.length;
      this.anteponerVelas(data);
      if (this.fullData.length === antes) return; // sin velas nuevas (ya deduplicadas) → nada más que traer
    }
  }

  setRange(period: string): void {
    if (!this.lwChart) return;
    if (period === 'all') {
      if (this.isIntraday && this.fullData.length && !this.rangeLoading) {
        this.rangeLoading = true;
        this.cargarHistorialCompletoIntradia().finally(() => {
          this.rangeLoading = false;
          try { this.lwChart?.timeScale().fitContent(); } catch {}
        });
      } else {
        try { this.lwChart.timeScale().fitContent(); } catch {}
      }
      return;
    }
    if (!this.fullData.length) return;
    const last = new Date(this.fullData[this.fullData.length - 1][0]).toISOString().slice(0, 10);
    const days = period === '1M' ? 30 : period === '3M' ? 90 : period === '6M' ? 180 : 365;
    const from = new Date(new Date(last).getTime() - days * 86400000).toISOString().slice(0, 10);
    try { this.lwChart.timeScale().setVisibleRange({ from: from as any, to: last as any }); } catch {}
  }

  // ── Theme ─────────────────────────────────────────────────────────────────

  private updateChartTheme(): void {
    if (!this.lwChart) return;
    const c = this.getColors();
    this.lwChart.applyOptions({
      layout: { background: { type: ColorType.Solid, color: c.bg }, textColor: c.text },
      grid: { vertLines: { color: c.grid }, horzLines: { color: c.grid } },
      rightPriceScale: { borderColor: c.border },
      timeScale: { borderColor: c.border },
    });
    if (this.candleSeries) {
      this.candleSeries.applyOptions({
        upColor: c.upColor, downColor: c.downColor,
        wickUpColor: c.upColor, wickDownColor: c.downColor,
      });
    }
    if (this.volumeSeries && this.fullData.length) {
      this.volumeSeries.setData(this.convertVolume(this.fullData, c.upColor, c.downColor));
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private getColors() {
    return {
      bg: this.isDark ? '#1e222d' : '#ffffff',
      text: this.isDark ? '#d1d4dc' : '#000000',
      grid: this.isDark ? '#2a2e39' : '#e6e6e6',
      border: this.isDark ? '#3a3f4b' : '#ccd6eb',
      upColor: '#26a69a',
      downColor: '#ef5350',
    };
  }

  /** Intradía: segundos unix; diario: 'YYYY-MM-DD' (formato de tiempo de lightweight-charts). */
  private toTime(ms: number): string | number {
    if (this.isIntraday) return Math.round(ms / 1000);
    return new Date(ms).toISOString().slice(0, 10);
  }

  private compareTime(a: string | number, b: string | number): number {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b));
  }

  private convertOhlc(rows: number[][]): any[] {
    if (!rows.length) return [];
    return rows
      .map(r => ({ time: this.toTime(Number(r[0])), open: Number(r[1]), high: Number(r[2]), low: Number(r[3]), close: Number(r[4]) }))
      .filter(d => !isNaN(d.open) && !isNaN(d.close))
      .sort((a, b) => this.compareTime(a.time, b.time))
      .reduce((acc: any[], d) => { if (!acc.length || acc[acc.length - 1].time !== d.time) acc.push(d); return acc; }, []);
  }

  private convertVolume(rows: number[][], upColor: string, downColor: string): any[] {
    if (!rows.length) return [];
    return rows
      .map((r, i) => ({
        time: this.toTime(Number(r[0])),
        value: isFinite(Number(r[r.length - 1])) ? Number(r[r.length - 1]) : 0,
        color: Number(r[4]) >= (i > 0 ? Number(rows[i - 1][4]) : Number(r[4])) ? upColor + '88' : downColor + '88',
      }))
      .sort((a, b) => this.compareTime(a.time, b.time))
      .reduce((acc: any[], d) => { if (!acc.length || acc[acc.length - 1].time !== d.time) acc.push(d); return acc; }, []);
  }

  private destroyChart(): void {
    if (this.resizeObserver) { this.resizeObserver.disconnect(); this.resizeObserver = null; }
    this.drawingCtl.dispose();
    if (this.lwChart) {
      try { this.lwChart.remove(); } catch {}
      this.lwChart = null;
    }
    this.candleSeries = null;
    this.volumeSeries = null;
  }

  ngOnDestroy(): void {
    this.subDarktema.unsubscribe();
    this.lotesSub.unsubscribe();
    this.detenerActualizacion();
    this.cancelarDescarga();
    this.destroyChart();
  }
}
